/* eslint-disable -- React Compiler rules (set-state-in-effect) used by the audit tool are not registered in CRA's bundled eslint-plugin-react-hooks v5; referencing them by name causes the dev server to fail. The flagged useEffects perform legitimate setState calls that are required to mirror server / context state into the component. */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CreditCard, FileText, X, ShieldAlert, Star, MessageCircle } from 'lucide-react';
import { usePageSeo } from './seo';
import { RichTextEditor, RichTextView, sanitizeDescriptionHtml, previewText } from './richText';
import logger from './logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============================================================================
// EventDetailsPage sub-components (Phase 1 split — pure refactor, no behaviour
// change). Kept as internal components inside Pages.js so the rest of the app
// requires no import churn.
// ============================================================================

function EventHeader({ event }) {
  return (
    <>
      {event.cover_image && (
        <div className="w-full max-w-md mx-auto aspect-[4/5] mb-6 rounded-xl shadow-custom overflow-hidden">
          <img
            src={event.cover_image}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {event.status === 'cancelled' && (
          <span className="px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-bold">
            EVENT CANCELLED
          </span>
        )}
        <span className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-sm font-medium" data-testid="event-category">
          {event.category}
        </span>
        {event.is_free && (
          <span className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm font-medium" data-testid="event-free-badge">Free</span>
        )}
      </div>

      <h1 className="text-4xl font-bold mb-4" data-testid="event-title">{event.title}</h1>

      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 flex-wrap">
        <span>Organised by {event.owner?.name || 'Unknown'}</span>
        {event.owner?.verification_status === 'verified' ? (
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-full" data-testid="verified-badge">
            <Shield size={14} className="text-blue-600" />
            <span className="text-xs font-medium text-blue-800">Verified Organiser</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-full" data-testid="unverified-badge">
            <ShieldAlert size={14} className="text-gray-500" />
            <span className="text-xs font-medium text-gray-600">Unverified</span>
          </div>
        )}
        {event.owner?.is_launch_partner && (
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-full"
            style={{ backgroundColor: '#FAEBCD', border: '1px solid #C08A2E' }}
            data-testid="launch-partner-badge"
          >
            <Star size={14} style={{ color: '#8A5A00' }} />
            <span className="text-xs font-medium" style={{ color: '#8A5A00' }}>Launch Partner</span>
          </div>
        )}
      </div>
    </>
  );
}

function EventInfo({ event, onReport }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-muted-foreground">
        <div className="flex items-start gap-2">
          <span className="text-xl">📅</span>
          <div>
            <div className="font-medium text-foreground">Date</div>
            <div>{new Date(event.date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-xl">🕐</span>
          <div>
            <div className="font-medium text-foreground">Time</div>
            <div>{event.time}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-xl">📍</span>
          <div>
            <div className="font-medium text-foreground">Location</div>
            <div>{event.town_or_city}, {event.county_or_state}</div>
            {event.address && (
              <div className="text-sm mt-1" data-testid="event-address">{event.address}</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card p-6 rounded-xl shadow-custom mb-6">
        <h2 className="text-xl font-semibold mb-3">About This Event</h2>
        <RichTextView html={event.description} testId="event-description" />
        <div className="mt-4 pt-4 border-t border-border">
          <button
            onClick={onReport}
            className="text-sm text-muted-foreground hover:text-foreground transition underline"
            data-testid="report-event-button"
          >
            Report this event
          </button>
        </div>
      </div>

      <div className="bg-card p-6 rounded-xl shadow-custom mb-6" data-testid="price-breakdown-card">
        <h3 className="text-lg font-semibold mb-4">Ticket Price</h3>
        {event.is_free ? (
          <div className="text-base" data-testid="price-breakdown-free">
            <strong>Free</strong> — no booking fee
          </div>
        ) : (
          (() => {
            const ticketPrice = Number(event.price) || 0;
            const feeStructure = event.fee_structure === 'passed' ? 'passed' : 'absorbed';
            const serviceFee = Math.round(ticketPrice * 0.05 * 100) / 100;
            const total = feeStructure === 'passed'
              ? Math.round((ticketPrice + serviceFee) * 100) / 100
              : ticketPrice;
            const fmt = (n) => Number(n).toFixed(2);
            const sym = event.currency === 'NGN' ? '₦' : '£';
            return (
              <div className="space-y-2 text-base" data-testid="price-breakdown-paid">
                <div className="flex justify-between" data-testid="price-line-ticket">
                  <span>Ticket price:</span>
                  <span>{sym}{fmt(ticketPrice)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground" data-testid="price-line-fee">
                  <span>Gatherly service fee (5%):</span>
                  <span>{sym}{fmt(serviceFee)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-border pt-2 mt-2" data-testid="price-line-total">
                  <span>Total you pay:</span>
                  <span>{sym}{fmt(total)}</span>
                </div>
              </div>
            );
          })()
        )}
      </div>

      <div className="bg-card p-6 rounded-xl shadow-custom mb-6">
        <h3 className="text-lg font-semibold mb-4">Attendee Protection</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Shield size={24} className="text-primary mt-1" />
            <div>
              <div className="font-medium mb-1">Verified Organisers</div>
              <div className="text-sm text-muted-foreground">All event organisers are verified</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <FileText size={24} className="text-primary mt-1" />
            <div>
              <button
                onClick={() => navigate('/refund-policy')}
                className="font-medium mb-1 hover:underline text-left"
                data-testid="event-refund-policy-link"
              >
                Gatherly Refund Policy
              </button>
              <div className="text-sm text-muted-foreground">Full protection included</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function RSVPSection({ event, user, eventId }) {
  const navigate = useNavigate();
  const [rsvpCount, setRsvpCount] = useState(0);
  const [hasRsvped, setHasRsvped] = useState(false);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [rsvpMessage, setRsvpMessage] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(user?.marketing_consent);
  const [marketingPromptDismissed, setMarketingPromptDismissed] = useState(false);
  const [marketingBusy, setMarketingBusy] = useState(false);

  const handleMarketingConsent = async (consent) => {
    if (marketingBusy) return;
    setMarketingBusy(true);
    try {
      if (consent) {
        const res = await fetch(`${API_URL}/api/users/me/marketing-consent`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ consent: true })
        });
        if (res.ok) setMarketingConsent(true);
      }
    } catch (err) {
      logger.debug('marketing consent update failed', err);
    } finally {
      setMarketingPromptDismissed(true);
      setMarketingBusy(false);
    }
  };

  const loadRsvpState = async () => {
    try {
      const cRes = await fetch(`${API_URL}/api/events/${eventId}/rsvp-count`);
      if (cRes.ok) {
        const c = await cRes.json();
        setRsvpCount(Number(c.count) || 0);
      }
    } catch (err) {
      logger.debug('rsvp count fetch failed', err);
    }
    if (user) {
      try {
        const mRes = await fetch(`${API_URL}/api/events/${eventId}/rsvp`, { credentials: 'include' });
        if (mRes.ok) {
          const m = await mRes.json();
          setHasRsvped(!!m.rsvped);
        }
      } catch (err) {
        logger.debug('my-rsvp lookup failed', err);
      }
    } else {
      setHasRsvped(false);
    }
  };
  useEffect(() => {
    loadRsvpState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, user]);

  const logReferralClick = async (clickType) => {
    try {
      await fetch(`${API_URL}/api/referral-clicks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event_id: eventId, click_type: clickType })
      });
    } catch (err) {
      logger.error('Referral click logging failed:', err);
    }
  };

  const handleGetTickets = (url) => {
    logReferralClick('website');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleWhatsAppContact = (waUrl) => {
    logReferralClick('whatsapp');
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const handleReserveFreePlace = async () => {
    if (!user) {
      try {
        sessionStorage.setItem('gatherly_signup_message', 'Create a free account to reserve your place at this event.');
      } catch (err) {
        logger.debug('sessionStorage unavailable', err);
      }
      navigate('/signup');
      return;
    }
    setRsvpBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/rsvp`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setHasRsvped(true);
        setRsvpMessage("You're going! Your place is reserved.");
        await loadRsvpState();
      } else {
        const d = await res.json().catch(() => ({}));
        setRsvpMessage(d.detail || 'Could not reserve your place. Please try again.');
      }
    } catch (err) {
      logger.error('RSVP network error', err);
      setRsvpMessage('Network error. Please try again.');
    } finally {
      setRsvpBusy(false);
    }
  };

  const handleCancelRsvp = async () => {
    if (!user) return;
    setRsvpBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/rsvp`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setHasRsvped(false);
        setRsvpMessage('');
        await loadRsvpState();
      }
    } catch (err) {
      logger.debug('cancel-rsvp network error', err);
    }
    setRsvpBusy(false);
  };

  if (event.is_free) {
    return (
      <div className="bg-card p-6 rounded-xl shadow-custom mb-6" data-testid="free-rsvp-section">
        <h3 className="text-lg font-semibold mb-3">Attend this free event</h3>
        {event.status === 'cancelled' ? (
          <p className="text-sm text-muted-foreground" data-testid="event-cancelled-notice">This event has been cancelled.</p>
        ) : (
          <>
            {hasRsvped ? (
              <button
                onClick={handleCancelRsvp}
                disabled={rsvpBusy}
                className="px-6 py-3 border-2 border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-primary-foreground transition disabled:opacity-60"
                data-testid="cancel-rsvp-button"
              >
                Cancel my RSVP
              </button>
            ) : (
              <button
                onClick={handleReserveFreePlace}
                disabled={rsvpBusy}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition disabled:opacity-60"
                data-testid="reserve-free-place-button"
              >
                Reserve my free place
              </button>
            )}
            {rsvpMessage && (
              <p className="text-sm mt-3 font-medium" style={{ color: '#3B2314' }} data-testid="rsvp-message">{rsvpMessage}</p>
            )}
            {hasRsvped && user && marketingConsent === false && !marketingPromptDismissed && (
              <div
                className="mt-4 p-4 rounded-lg border"
                style={{ borderColor: '#C4A57B', backgroundColor: '#F5ECD7' }}
                data-testid="marketing-consent-prompt"
              >
                <p className="text-sm mb-3" style={{ color: '#3B2314' }}>
                  Would you like us to send you similar events and Gatherly news?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMarketingConsent(true)}
                    disabled={marketingBusy}
                    className="px-4 py-2 rounded-md text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
                    style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
                    data-testid="marketing-consent-yes"
                  >
                    Yes please
                  </button>
                  <button
                    onClick={() => handleMarketingConsent(false)}
                    disabled={marketingBusy}
                    className="px-4 py-2 rounded-md text-sm font-medium border border-border hover:bg-muted transition disabled:opacity-60"
                    data-testid="marketing-consent-no"
                  >
                    No thanks
                  </button>
                </div>
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-3" data-testid="attendee-count">
              {rsvpCount} {rsvpCount === 1 ? 'person is' : 'people are'} going.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card p-6 rounded-xl shadow-custom mb-6" data-testid="paid-tickets-section">
      <h3 className="text-lg font-semibold mb-3">Tickets</h3>
      {event.status === 'cancelled' ? (
        <p className="text-sm text-muted-foreground">This event has been cancelled.</p>
      ) : (
        (() => {
          const externalLink = (event.external_link || '').trim();
          const whatsappRaw = (event.whatsapp_number || '').trim();
          const waNumber = whatsappRaw.replace(/[^\d+]/g, '').replace(/^\+/, '');
          const waUrl = waNumber ? `https://wa.me/${waNumber}` : '';
          const hasLink = !!externalLink;
          const hasWa = !!waNumber;
          if (!hasLink && !hasWa) {
            return (
              <div data-testid="ticket-email-fallback">
                <p className="text-sm text-muted-foreground mb-2">Contact organiser to purchase tickets.</p>
                <a
                  href={`mailto:${event.owner?.email || ''}`}
                  className="text-primary hover:underline font-medium"
                  data-testid="ticket-email-link"
                >
                  {event.owner?.email || 'Email unavailable'}
                </a>
              </div>
            );
          }
          return (
            <div className="flex flex-wrap gap-3" data-testid="ticket-buttons-row">
              {hasLink && (
                <button
                  onClick={() => handleGetTickets(externalLink)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition"
                  data-testid="get-tickets-button"
                >
                  Get Tickets
                </button>
              )}
              {hasWa && (
                <button
                  onClick={() => handleWhatsAppContact(waUrl)}
                  className="px-6 py-3 border-2 border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-primary-foreground transition"
                  data-testid="contact-organiser-button"
                >
                  Contact Organiser for Tickets
                </button>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
}

export function EventDetailsPage({ user }) {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({ reason: '', description: '' });
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const navigate = useNavigate();

  usePageSeo(event ? {
    title: `${event.title} in ${event.town_or_city || event.county_or_state || ''} | Gatherly`,
    description: previewText(event.description, 155),
  } : {});

  useEffect(() => {
    fetch(`${API_URL}/api/events/${eventId}`)
      .then(res => res.json())
      .then(setEvent)
      .catch(err => logger.error('Error loading event:', err));
  }, [eventId]);

  const copyEventLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareToWhatsApp = () => {
    const url = window.location.href;
    const text = `Check out: ${event.title}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
  };

  const handleReport = async () => {
    if (!user) {
      alert('Please login to report an event');
      navigate('/login');
      return;
    }

    if (!reportData.reason || !reportData.description.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_id: eventId,
          reason: reportData.reason,
          description: reportData.description
        })
      });

      if (response.ok) {
        alert('Report submitted successfully. We will review it shortly.');
        setShowReportModal(false);
        setReportData({ reason: '', description: '' });
      } else {
        alert('Failed to submit report. Please try again.');
      }
    } catch (error) {
      logger.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    }
  };


  if (!event) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-4">⏳</div>
        <p className="text-muted-foreground">Loading event details...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <EventHeader event={event} />
        <EventInfo event={event} onReport={() => setShowReportModal(true)} />
        <RSVPSection event={event} user={user} eventId={eventId} />

        {/* Share Section */}
        <div className="bg-card p-6 rounded-xl shadow-custom mb-6">
          <h3 className="text-lg font-semibold mb-4">Share This Event</h3>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={copyEventLink}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition" 
              data-testid="copy-link"
            >
              {copied ? (
                <>✓ Link Copied!</>
              ) : (
                <>🔗 Copy Link</>
              )}
            </button>
            <button 
              onClick={shareToWhatsApp}
              className="flex items-center gap-2 px-6 py-3 border border-border rounded-lg font-medium hover:bg-accent transition" 
              data-testid="share-whatsapp"
            >
              💬 Share on WhatsApp
            </button>
          </div>
        </div>

        {/* Event Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="bg-card p-6 rounded-xl shadow-custom">
            <h3 className="text-lg font-semibold mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {event.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Gatherly Support Footer */}
        <div
          className="mt-8 p-4 rounded-xl border text-center"
          style={{ borderColor: '#E0CFA6', backgroundColor: '#F5ECD7' }}
          data-testid="event-support-footer"
        >
          <p className="text-sm mb-3" style={{ color: '#3B2314' }}>
            Need help with this event? Contact Gatherly support on WhatsApp
          </p>
          <a
            href="https://wa.me/447405880502"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="event-support-whatsapp-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition text-sm"
            style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
          >
            <MessageCircle className="w-4 h-4" />
            Chat with Gatherly Support
          </a>
        </div>
      </div>

      {/* Checkout Confirmation Modal */}
      {showCheckoutModal && !event.is_free && (() => {
        const ticketPrice = Number(event.price) || 0;
        const feeStructure = event.fee_structure === 'passed' ? 'passed' : 'absorbed';
        const serviceFee = Math.round(ticketPrice * 0.05 * 100) / 100;
        const total = feeStructure === 'passed'
          ? Math.round((ticketPrice + serviceFee) * 100) / 100
          : ticketPrice;
        const fmt = (n) => Number(n).toFixed(2);
        const sym = event.currency === 'NGN' ? '₦' : '£';
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="checkout-modal">
            <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-custom">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Confirm Your Order</h3>
                <button onClick={() => setShowCheckoutModal(false)} className="hover:opacity-70" data-testid="checkout-close">
                  <X size={24} />
                </button>
              </div>

              <div className="mb-4">
                <div className="text-sm text-muted-foreground mb-1">Event</div>
                <div className="font-medium">{event.title}</div>
              </div>

              <div className="space-y-2 text-base mb-4" data-testid="checkout-price-breakdown">
                <div className="flex justify-between" data-testid="checkout-line-ticket">
                  <span>Ticket price:</span>
                  <span>{sym}{fmt(ticketPrice)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground" data-testid="checkout-line-fee">
                  <span>Gatherly service fee (5%):</span>
                  <span>{sym}{fmt(serviceFee)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-border pt-2 mt-2" data-testid="checkout-line-total">
                  <span>Total you pay:</span>
                  <span>{sym}{fmt(total)}</span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-5" data-testid="checkout-fee-explainer">
                The Gatherly service fee supports platform maintenance and attendee protection.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    alert(`Proceeding to secure payment for ${sym}${fmt(total)}.`);
                    setShowCheckoutModal(false);
                  }}
                  className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition"
                  data-testid="checkout-confirm-pay"
                >
                  Confirm &amp; Pay {sym}{fmt(total)}
                </button>
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition"
                  data-testid="checkout-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Report Event Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-custom">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Report This Event</h3>
              <button onClick={() => setShowReportModal(false)} className="hover:opacity-70">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Reason for Report</label>
                <select 
                  value={reportData.reason}
                  onChange={(e) => setReportData({...reportData, reason: e.target.value})}
                  className="w-full px-4 py-2 border border-border rounded-lg"
                  required
                  data-testid="report-reason-select"
                >
                  <option value="">Select a reason...</option>
                  <option value="Event cancelled without notice">Event cancelled without notice</option>
                  <option value="Event details are inaccurate">Event details are inaccurate</option>
                  <option value="Suspected fake event">Suspected fake event</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description (max 300 characters)</label>
                <textarea 
                  value={reportData.description}
                  onChange={(e) => setReportData({...reportData, description: e.target.value.slice(0, 300)})}
                  className="w-full px-4 py-2 border border-border rounded-lg h-32 resize-none"
                  placeholder="Please provide details about your report..."
                  maxLength={300}
                  required
                  data-testid="report-description-textarea"
                />
                <div className="text-xs text-muted-foreground text-right mt-1">
                  {reportData.description.length}/300
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={handleReport}
                  className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition"
                  data-testid="report-submit-button"
                >
                  Submit Report
                </button>
                <button 
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-6 py-3 border border-border rounded-lg font-medium hover:bg-muted transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CreateEventPage({ user }) {
  const { eventId } = useParams();
  const isEditMode = Boolean(eventId);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', category: '', tags: '', labels: '',
    location_type: 'uk', county_or_state: '', town_or_city: '', address: '',
    date: '', time: '', end_time: '',
    is_free: true, price: 0, currency: 'GBP', fee_structure: 'absorbed',
    external_link: '', whatsapp_number: '',
    is_private: false,
    cover_image: ''
  });
  const [showPriceWarning, setShowPriceWarning] = useState(false);
  const [coverImageError, setCoverImageError] = useState('');
  const [coverImageProcessing, setCoverImageProcessing] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEditMode);
  const [loadError, setLoadError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(res => res.json())
      .then(data => setCategories(data.categories || data));
  }, []);

  // Pre-fill the form when editing an existing event
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/events/${eventId}`, { credentials: 'include' });
        if (!res.ok) {
          setLoadError('Could not load this event.');
          setLoadingEvent(false);
          return;
        }
        const ev = await res.json();
        if (cancelled) return;
        setFormData({
          title: ev.title || '',
          description: ev.description || '',
          category: ev.category || '',
          tags: Array.isArray(ev.tags) ? ev.tags.join(', ') : (ev.tags || ''),
          labels: Array.isArray(ev.labels) ? ev.labels.join(', ') : (ev.labels || ''),
          location_type: ev.location_type || 'uk',
          county_or_state: ev.county_or_state || '',
          town_or_city: ev.town_or_city || '',
          address: ev.address || '',
          date: ev.date || '',
          time: ev.time || '',
          end_time: ev.end_time || '',
          is_free: ev.is_free !== undefined ? ev.is_free : true,
          price: ev.price ?? 0,
          currency: ev.currency || 'GBP',
          fee_structure: ev.fee_structure || 'absorbed',
          external_link: ev.external_link || '',
          whatsapp_number: ev.whatsapp_number || '',
          is_private: !!ev.is_private,
          cover_image: ev.cover_image || ''
        });
        setLoadingEvent(false);
      } catch (err) {
        logger.error('Error loading event:', err);
        setLoadError('Could not load this event.');
        setLoadingEvent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, isEditMode]);

  // Client-side: validate → resize to max width 1200px → compress as JPEG (quality 0.85) → base64 data URL
  const handleCoverImageChange = (e) => {
    setCoverImageError('');
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
    if (!ALLOWED.includes(file.type)) {
      setCoverImageError('Only JPG, PNG, or WEBP images are allowed.');
      e.target.value = '';
      return;
    }
    const MAX_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setCoverImageError('Image is larger than 5MB. Please upload a smaller file.');
      e.target.value = '';
      return;
    }

    setCoverImageProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Center-crop to 4:5 aspect ratio (portrait), then resize to max 1200×1500 without upscaling
        const TARGET_RATIO = 4 / 5; // width / height
        const srcW = img.width;
        const srcH = img.height;
        const srcRatio = srcW / srcH;
        let cropW, cropH, sx, sy;
        if (srcRatio > TARGET_RATIO) {
          // Source is too wide — crop horizontally
          cropH = srcH;
          cropW = Math.round(srcH * TARGET_RATIO);
          sx = Math.round((srcW - cropW) / 2);
          sy = 0;
        } else {
          // Source is too tall (or exact) — crop vertically
          cropW = srcW;
          cropH = Math.round(srcW / TARGET_RATIO);
          sy = Math.round((srcH - cropH) / 2);
          sx = 0;
        }
        // Do not upscale — final size never exceeds cropped native pixels
        const MAX_WIDTH = 1200;
        const targetWidth = Math.min(cropW, MAX_WIDTH);
        const targetHeight = Math.round(targetWidth / TARGET_RATIO);
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        // Fill white so transparent PNGs → JPEG don't come out black
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, targetWidth, targetHeight);
        try {
          const compressed = canvas.toDataURL('image/jpeg', 0.85);
          setFormData((prev) => ({ ...prev, cover_image: compressed }));
        } catch (err) {
          setCoverImageError('Could not process this image. Please try another file.');
        }
        setCoverImageProcessing(false);
      };
      img.onerror = () => {
        setCoverImageError('Could not read this image. Please try another file.');
        setCoverImageProcessing(false);
      };
      img.src = ev.target.result;
    };
    reader.onerror = () => {
      setCoverImageError('Could not read this file. Please try again.');
      setCoverImageProcessing(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enforce cover image as a required field
    if (!formData.cover_image) {
      setCoverImageError('Please upload an event flyer or cover image before publishing.');
      // Scroll error into view so the organiser sees it
      const el = document.querySelector('[data-testid="event-cover-image-section"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Description is a rich-text HTML field — require some actual text content
    const sanitisedDescription = sanitizeDescriptionHtml(formData.description || '');
    const descriptionPlain = previewText(sanitisedDescription, 5000);
    if (!descriptionPlain.trim()) {
      alert('Please write a description for your event.');
      return;
    }

    const eventData = {
      ...formData,
      description: sanitisedDescription,
      tags: typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : formData.tags,
      labels: typeof formData.labels === 'string' ? formData.labels.split(',').map(t => t.trim()).filter(Boolean) : (formData.labels || []),
      end_time: formData.end_time ? formData.end_time : null,
      is_private: !!formData.is_private,
      password: '',
      capacity: null,
      rsvp_tracking_visible: true
    };

    try {
      const response = await fetch(
        isEditMode ? `${API_URL}/api/events/${eventId}` : `${API_URL}/api/events`,
        {
          method: isEditMode ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(eventData)
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (isEditMode) {
          alert('Event updated successfully!');
          navigate('/dashboard');
        } else {
          alert('Event created successfully!');
          navigate(`/event/${data.event_id}`);
        }
      } else {
        alert(`Error: ${data.detail || (isEditMode ? 'Failed to update event' : 'Failed to create event')}`);
      }
    } catch (error) {
      logger.error(isEditMode ? 'Error updating event:' : 'Error creating event:', error);
      alert(isEditMode ? 'Failed to update event. Please try again.' : 'Failed to create event. Please try again.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold mb-6" data-testid="create-event-heading">{isEditMode ? 'Edit Event' : 'Create Event'}</h1>
      {isEditMode && loadingEvent && (
        <p className="text-muted-foreground mb-4" data-testid="edit-event-loading">Loading event details…</p>
      )}
      {isEditMode && loadError && (
        <p className="text-red-600 mb-4" data-testid="edit-event-load-error">{loadError}</p>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input type="text" placeholder="Event Title" className="w-full p-3 border rounded" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="event-title-input" />
        <div>
          <label className="block text-sm font-medium mb-2" htmlFor="event-description-editor">Description</label>
          <RichTextEditor
            testId="event-description-editor"
            value={formData.description}
            onChange={(html) => setFormData({ ...formData, description: html })}
            placeholder="Tell attendees what to expect…"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Use the toolbar to add <strong>bold</strong>, <em>italic</em>, <u>underline</u>, bullet or numbered lists.
          </p>
        </div>
        <select className="w-full p-3 border rounded" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required data-testid="event-category-select">
          <option value="">Select Category</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="text" placeholder="Tags" className="w-full p-3 border rounded" value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} data-testid="event-tags-input" />

        {/* Event Flyer / Cover Image */}
        <div
          className="border border-border rounded-lg p-4 bg-card space-y-3"
          data-testid="event-cover-image-section"
        >
          <label className="block font-medium" htmlFor="event-cover-image-input">
            Event flyer or cover image <span className="text-red-600">*</span>
          </label>
          <p className="text-xs text-muted-foreground">
            JPG, PNG, or WEBP. Maximum 5MB. We&apos;ll automatically resize wide images to 1200px for fast loading.
          </p>
          <input
            id="event-cover-image-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleCoverImageChange}
            className="block w-full text-sm"
            data-testid="event-cover-image-input"
          />
          {coverImageProcessing && (
            <p className="text-sm text-muted-foreground" data-testid="event-cover-image-processing">
              Processing image…
            </p>
          )}
          {coverImageError && (
            <p className="text-sm text-red-600" data-testid="event-cover-image-error">
              {coverImageError}
            </p>
          )}
          {formData.cover_image && !coverImageProcessing && (
            <div className="mt-2" data-testid="event-cover-image-preview-wrapper">
              <p className="text-xs text-muted-foreground mb-2">Preview (cropped to 4:5):</p>
              <div className="w-48 aspect-[4/5] rounded-md border border-border overflow-hidden">
                <img
                  src={formData.cover_image}
                  alt="Event cover preview"
                  className="w-full h-full object-cover"
                  data-testid="event-cover-image-preview"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, cover_image: '' }));
                  setCoverImageError('');
                  const inp = document.getElementById('event-cover-image-input');
                  if (inp) inp.value = '';
                }}
                className="mt-2 text-sm underline hover:opacity-80"
                style={{ color: '#3B2314' }}
                data-testid="event-cover-image-remove"
              >
                Remove image
              </button>
            </div>
          )}
        </div>

        {/* Location Section */}
        <div className="border border-border rounded-lg p-4 bg-card space-y-3" data-testid="location-section">
          <div className="font-medium">Location</div>
          <div>
            <label className="block text-sm mb-1">Country</label>
            <select
              className="w-full p-3 border rounded"
              value={formData.location_type}
              onChange={(e) => {
                const lt = e.target.value;
                setFormData({
                  ...formData,
                  location_type: lt,
                  currency: lt === 'nigeria' ? 'NGN' : 'GBP'
                });
              }}
              data-testid="event-country-select"
              required
            >
              <option value="uk">United Kingdom</option>
              <option value="nigeria">Nigeria</option>
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">{formData.location_type === 'nigeria' ? 'State' : 'County / Region'} <span className="text-destructive">*</span></label>
            <input
              type="text"
              placeholder={formData.location_type === 'nigeria' ? 'e.g. Lagos State' : 'e.g. Greater London'}
              className="w-full p-3 border rounded"
              value={formData.county_or_state}
              onChange={(e) => setFormData({...formData, county_or_state: e.target.value})}
              required
              data-testid="event-county-input"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Location city <span className="text-destructive">*</span></label>
            <input
              type="text"
              placeholder={formData.location_type === 'nigeria' ? 'e.g. Lagos' : 'e.g. London'}
              className="w-full p-3 border rounded"
              value={formData.town_or_city}
              onChange={(e) => setFormData({...formData, town_or_city: e.target.value})}
              required
              data-testid="event-city-input"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Full address <span className="text-destructive">*</span></label>
            <input
              type="text"
              placeholder="Street, building, postcode"
              className="w-full p-3 border rounded"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              required
              data-testid="event-address-input"
            />
          </div>
        </div>

        <input type="date" className="w-full p-3 border rounded" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required data-testid="event-date-input" />
        <input type="time" className="w-full p-3 border rounded" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} required data-testid="event-time-input" />
        
        {/* Price Section with Verification Gating */}
        <div>
          <label className="block mb-2 font-medium">Is the event free?</label>
          <select 
            className="w-full p-3 border rounded" 
            value={formData.is_free} 
            onChange={(e) => {
              const isFree = e.target.value === 'true';
              setFormData({...formData, is_free: isFree, price: isFree ? 0 : formData.price});
              if (!isFree && user?.verification_status !== 'verified') {
                setShowPriceWarning(true);
              } else {
                setShowPriceWarning(false);
              }
            }}
          >
            <option value="true">Yes, it&apos;s free</option>
            <option value="false">No, it&apos;s a paid event</option>
          </select>
        </div>
        
        {!formData.is_free && (
          <div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block mb-2 font-medium">Currency <span className="text-destructive">*</span></label>
                <select
                  className="w-full p-3 border rounded"
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  required
                  data-testid="event-currency-select"
                >
                  <option value="GBP">GBP (£)</option>
                  <option value="NGN">NGN (₦)</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block mb-2 font-medium">Ticket Price ({formData.currency === 'NGN' ? '₦' : '£'}) <span className="text-destructive">*</span></label>
                <input 
                  type="number" 
                  className="w-full p-3 border rounded" 
                  value={formData.price} 
                  onChange={(e) => {
                    const price = parseFloat(e.target.value);
                    setFormData({...formData, price});
                    if (price > 0 && user?.verification_status !== 'verified') {
                      setShowPriceWarning(true);
                    } else {
                      setShowPriceWarning(false);
                    }
                  }}
                  min="0" 
                  step="0.01"
                  required
                  data-testid="event-price-input"
                />
              </div>
            </div>
            {showPriceWarning && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="price-warning">
                <p className="text-sm text-yellow-800">
                  <strong>To sell paid tickets you must be a verified organiser.</strong> Free events can be posted immediately.{' '}
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/dashboard');
                    }}
                    className="underline font-semibold hover:text-yellow-900"
                  >
                    Get verified now
                  </button>
                </p>
              </div>
            )}

            {/* Gatherly Service Fee Choice */}
            <div className="mt-4 p-4 border border-border rounded-lg bg-card" data-testid="fee-structure-section">
              <label className="block mb-3 font-medium">Who pays the Gatherly service fee?</label>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="fee-structure-absorbed-label">
                  <input
                    type="radio"
                    name="fee_structure"
                    value="absorbed"
                    checked={formData.fee_structure === 'absorbed'}
                    onChange={() => setFormData({ ...formData, fee_structure: 'absorbed' })}
                    className="mt-1"
                    data-testid="fee-structure-absorbed"
                  />
                  <span className="text-sm">
                    <strong>I absorb the fee</strong> — attendees pay the ticket price only, I receive 95% of each ticket.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="fee-structure-passed-label">
                  <input
                    type="radio"
                    name="fee_structure"
                    value="passed"
                    checked={formData.fee_structure === 'passed'}
                    onChange={() => setFormData({ ...formData, fee_structure: 'passed' })}
                    className="mt-1"
                    data-testid="fee-structure-passed"
                  />
                  <span className="text-sm">
                    <strong>Pass fee to attendees</strong> — attendees pay ticket price plus 5%, I receive the full ticket price.
                  </span>
                </label>
              </div>
              <p
                className="mt-3 text-xs italic"
                style={{ color: '#3B2314' }}
                data-testid="fee-structure-free-note"
              >
                Gatherly is currently free for all organisers. This selection will apply when ticket processing is introduced.
              </p>
            </div>
          </div>
        )}

        {/* Ticket contact fields — optional for all events (free or paid) */}
        <div className="p-4 border border-border rounded-lg bg-card space-y-3" data-testid="ticket-contact-section">
          <div className="font-medium">Where can attendees buy tickets or contact you?</div>
          <p className="text-xs text-muted-foreground">
            Optional for all events. Provide a ticketing link, a WhatsApp number, or both. If neither is provided, attendees will see your email as the contact fallback.
          </p>
          <div>
            <label className="block text-sm mb-1">External ticket link (Eventbrite, Tixr, your own page, etc.)</label>
            <input
              type="url"
              placeholder="https://"
              value={formData.external_link}
              onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
              className="w-full p-3 border rounded"
              data-testid="event-external-link-input"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">WhatsApp number (international format, e.g. +447123456789)</label>
            <input
              type="text"
              placeholder="+447123456789"
              value={formData.whatsapp_number}
              onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
              className="w-full p-3 border rounded"
              data-testid="event-whatsapp-input"
            />
          </div>
        </div>

        {isEditMode && (
          <div className="p-4 border border-border rounded-lg bg-card" data-testid="event-visibility-section">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="event-private-toggle-label">
              <input
                type="checkbox"
                checked={!!formData.is_private}
                onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                data-testid="event-private-toggle"
              />
              <span className="text-sm">
                <strong>Private event</strong> — this event will not appear in the public discovery feed. Only people with the link can see it.
              </span>
            </label>
          </div>
        )}

        <button 
          type="submit" 
          className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
          data-testid="create-event-submit"
          disabled={showPriceWarning || (isEditMode && loadingEvent)}
        >
          {isEditMode ? 'Save Changes' : 'Create Event'}
        </button>
      </form>
    </div>
  );
}

export function ProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const navigate = useNavigate();
  usePageSeo({
    title: 'Discover Event Organisers | Gatherly',
    description: 'Meet the organisers behind the events on Gatherly — verified hosts creating experiences celebrating diaspora communities and local cultures worldwide.',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/profiles`).then(res => res.json()).then(setProfiles);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold mb-6">Discover Professionals</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {profiles.map(({ user: u, profile: p }) => (
          <div key={u.user_id} className="bg-card rounded-xl shadow-custom p-5 cursor-pointer hover:shadow-lg transition" onClick={() => navigate(`/profiles/${u.user_id}`)} data-testid={'profile-card-' + u.user_id}>
            <h3 className="font-semibold">{u.name}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {u.verification_status === 'verified' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-800" data-testid={'profile-verified-' + u.user_id}>
                  <Shield size={12} className="text-blue-600" />
                  Verified
                </span>
              )}
              {u.is_launch_partner && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: '#FAEBCD', border: '1px solid #C08A2E', color: '#8A5A00' }}
                  data-testid={'profile-launch-partner-' + u.user_id}
                >
                  <Star size={12} style={{ color: '#8A5A00' }} />
                  Launch Partner
                </span>
              )}
            </div>
            {p?.profession && <p className="text-sm text-muted-foreground mt-2">{p.profession}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProfileDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const profileRes = await fetch(`${API_URL}/api/profile/${userId}`);
        if (!profileRes.ok) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const profileData = await profileRes.json();
        if (cancelled) return;
        setData(profileData);

        // Fetch this user's public events
        try {
          const evRes = await fetch(`${API_URL}/api/events?owner_id=${userId}`);
          if (evRes.ok) {
            const all = await evRes.json();
            // Backend may not filter by owner; do a defensive client-side filter
            const mine = (Array.isArray(all) ? all : []).filter((e) => e.owner_id === userId);
            if (!cancelled) setEvents(mine);
          }
        } catch (err) {
          // ignore — events list is non-critical for the profile page
          logger.debug('owner events fetch failed', err);
        }
      } catch (err) {
        if (!cancelled) setNotFound(true);
        logger.debug('profile fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="profile-loading">
        <p className="text-muted-foreground">Loading profile…</p>
      </div>
    );
  }

  if (notFound || !data?.user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="profile-not-found">
        <h1 className="text-3xl font-bold mb-4">Profile not found</h1>
        <p className="text-muted-foreground mb-6">This profile does not exist or has been removed.</p>
        <button
          onClick={() => navigate('/profiles')}
          className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
          style={{ backgroundColor: '#3B2314', color: '#F5ECD7' }}
        >
          Back to Profiles
        </button>
      </div>
    );
  }

  const u = data.user;
  const p = data.profile || {};
  const interests = Array.isArray(p.interests) ? p.interests : [];
  const skills = Array.isArray(p.skills) ? p.skills : [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid={`profile-detail-${u.user_id}`}>
      {/* Header card */}
      <div className="bg-card rounded-xl shadow-custom p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2" data-testid="profile-name">{u.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {u.verification_status === 'verified' ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-800" data-testid="profile-verified-badge">
                  <Shield size={14} className="text-blue-600" />
                  Verified Organiser
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs text-gray-600" data-testid="profile-unverified-badge">
                  <ShieldAlert size={14} className="text-gray-500" />
                  Unverified
                </span>
              )}
              {u.is_launch_partner && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: '#FAEBCD', border: '1px solid #C08A2E', color: '#8A5A00' }}
                  data-testid="profile-launch-partner-badge"
                >
                  <Star size={14} style={{ color: '#8A5A00' }} />
                  Launch Partner
                </span>
              )}
            </div>
            {p.profession && (
              <p className="text-base text-muted-foreground" data-testid="profile-profession">{p.profession}</p>
            )}
          </div>
        </div>

        {/* Bio */}
        {p.bio && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-muted-foreground mb-1">About</h2>
            <p className="text-base leading-relaxed" data-testid="profile-bio">{p.bio}</p>
          </div>
        )}

        {/* Location */}
        {(p.location || p.city) && (
          <div className="mt-4 flex items-start gap-2">
            <span className="text-xl">📍</span>
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground">Location</h2>
              <p className="text-base" data-testid="profile-location">{p.location || p.city}</p>
            </div>
          </div>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-muted-foreground mb-2">Interests</h2>
            <div className="flex flex-wrap gap-2" data-testid="profile-interests">
              {interests.map((tag) => (
                <span key={tag} className="px-2 py-1 bg-accent text-accent-foreground rounded-full text-xs">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* Skills (if any) */}
        {skills.length > 0 && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-muted-foreground mb-2">Skills</h2>
            <div className="flex flex-wrap gap-2" data-testid="profile-skills">
              {skills.map((tag) => (
                <span key={tag} className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">{tag}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Events created */}
      <div className="bg-card rounded-xl shadow-custom p-6">
        <h2 className="text-2xl font-bold mb-4">Events by {u.name}</h2>
        {events.length === 0 ? (
          <p className="text-muted-foreground" data-testid="profile-no-events">
            {u.name} hasn&apos;t created any public events yet.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="profile-events-list">
            {events.map((ev) => (
              <div
                key={ev.event_id}
                className="border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/30 transition"
                onClick={() => navigate(`/event/${ev.event_id}`)}
                data-testid={`profile-event-${ev.event_id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs rounded-full">{ev.category}</span>
                  {ev.is_free && (
                    <span className="px-2 py-0.5 bg-secondary text-secondary-foreground text-xs rounded-full">Free</span>
                  )}
                </div>
                <h3 className="font-semibold">{ev.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {ev.date} {ev.town_or_city ? `· ${ev.town_or_city}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
